import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { LOGO_FULL, IMAGES } from "../lib/site";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 1.8 + i * 0.15, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = ({ onNavigate }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.28]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative flex h-screen min-h-[720px] items-center overflow-hidden"
    >
      {/* Parallax background */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 z-0"
      >
        <img
          src={IMAGES.skyline}
          alt="City skyline at dusk"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-eggshell/72" />
        <div className="absolute inset-0 bg-gradient-to-t from-eggshell via-eggshell/45 to-eggshell/65" />
      </motion.div>

      {/* Opening logo curtain */}
      <motion.div
        initial={{ opacity: 1 }}
        animate={{ opacity: 0 }}
        transition={{ duration: 1, delay: 1.6, ease: "easeInOut" }}
        className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center bg-eggshell"
      >
        <motion.img
          src={LOGO_FULL}
          alt="Metropolis Solutions LLC"
          initial={{ scale: 0.86, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="w-[280px] md:w-[380px]"
        />
      </motion.div>

      {/* Deco frame */}
      <div className="pointer-events-none absolute inset-6 z-20 border border-gold/25 md:inset-10" />
      <div className="pointer-events-none absolute inset-x-0 top-1/2 z-20 mx-auto h-px w-px" />

      {/* Content */}
      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-20 mx-auto w-full max-w-[1500px] px-8 md:px-16"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.9, duration: 1 }}
          className="mb-6 font-body text-xs uppercase tracking-[0.4em] text-gold"
          data-testid="hero-eyebrow"
        >
          Metropolis Solutions LLC · Government Contracting
        </motion.p>

        <h1
          className="font-display font-light leading-[0.92] text-ink"
          data-testid="hero-heading"
        >
          {["Securing the", "contracts that", "build empires."].map((t, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={line}
                initial="hidden"
                animate="show"
                className="block text-5xl sm:text-6xl md:text-7xl lg:text-8xl"
              >
                {i === 2 ? (
                  <>
                    build <span className="italic text-gold">empires.</span>
                  </>
                ) : (
                  t
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.5, duration: 1 }}
          className="mt-10 flex flex-col items-start gap-6 sm:flex-row sm:items-center"
        >
          <p className="max-w-md font-body text-base font-light text-ink/70">
            We find, qualify, and win federal opportunities — connecting ambitious
            businesses to awards of every size.
          </p>
          <button
            data-testid="hero-cta"
            onClick={() => onNavigate("#contact")}
            className="group relative overflow-hidden rounded-full border border-gold px-8 py-3.5 font-body text-xs uppercase tracking-[0.28em] text-gold transition-colors duration-500 hover:text-eggshell"
          >
            <span className="relative z-10">Start a Conversation</span>
            <span className="absolute inset-0 -translate-x-full bg-gold transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-x-0" />
          </button>
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-20 -translate-x-1/2 text-center"
      >
        <span className="mb-2 block font-body text-[10px] uppercase tracking-[0.3em] text-ink/50">
          Scroll
        </span>
        <div className="mx-auto h-10 w-px overflow-hidden bg-gold/20">
          <motion.div
            animate={{ y: [-40, 40] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            className="h-4 w-px bg-gold"
          />
        </div>
      </motion.div>
    </section>
  );
};
